// Categories management section — lives at the bottom of Settings.
// Lets users create / rename / recolor / delete habit categories. Used by
// HabitFormModal's category picker and by the colored chips on Today /
// Habits cards.
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Tag, Check, X } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';

const CATEGORY_COLORS = [
  '#8A9A86', '#7AA5C4', '#C8916A', '#B5838D', '#9B8AA6',
  '#7A8C72', '#C7A89D', '#5F8B95', '#A8A86B', '#8F6F8F',
];

export const CategoriesPanel = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(CATEGORY_COLORS[0]);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');

  const refresh = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('categories').select('*').order('created_at');
    if (error) toast.error(error.message);
    setItems(data || []);
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    const { error } = await supabase.from('categories').insert({
      user_id: user.id, name, color: newColor,
    });
    if (error) { toast.error(error.message); return; }
    setNewName('');
    setNewColor(CATEGORY_COLORS[0]);
    toast.success('Category added');
    refresh();
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditName(c.name);
    setEditColor(c.color);
  };
  const cancelEdit = () => { setEditingId(null); setEditName(''); setEditColor(''); };

  const saveEdit = async () => {
    const { error } = await supabase
      .from('categories')
      .update({ name: editName.trim(), color: editColor })
      .eq('id', editingId);
    if (error) { toast.error(error.message); return; }
    cancelEdit();
    toast.success('Category updated');
    refresh();
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? Habits assigned to this category will become uncategorised.`)) return;
    const { error } = await supabase.from('categories').delete().eq('id', c.id);
    if (error) { toast.error(error.message); return; }
    toast.success('Category deleted');
    refresh();
  };

  return (
    <section className="rounded-2xl border border-border bg-card/30 p-6 space-y-4" data-testid="settings-categories">
      <div className="flex items-center gap-3">
        <Tag className="h-4 w-4" />
        <div>
          <h2 className="font-heading font-medium">Categories</h2>
          <p className="text-xs text-muted-foreground">Group habits with a colored tag — appears on Today and Habits cards.</p>
        </div>
      </div>

      {/* New row */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="New category (e.g. Wellness)"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
          className="max-w-[260px]"
          data-testid="category-new-name"
        />
        <ColorRow value={newColor} onChange={setNewColor} testIdPrefix="category-new-color" />
        <Button onClick={create} disabled={!newName.trim()} className="rounded-full" data-testid="category-new-create">
          <Plus className="h-4 w-4" /> Add
        </Button>
      </div>

      {/* Existing list */}
      {loading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-muted-foreground">No categories yet. Create one above to start tagging habits.</p>
      ) : (
        <div className="space-y-1.5">
          {items.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-background/40 px-3 py-2" data-testid={`category-row-${c.id}`}>
              {editingId === c.id ? (
                <>
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="max-w-[200px] h-8"
                    autoFocus
                    data-testid={`category-edit-name-${c.id}`}
                  />
                  <ColorRow value={editColor} onChange={setEditColor} testIdPrefix={`category-edit-color-${c.id}`} />
                  <Button size="sm" onClick={saveEdit} className="rounded-full h-8" data-testid={`category-save-${c.id}`}>
                    <Check className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={cancelEdit} className="rounded-full h-8" data-testid={`category-cancel-${c.id}`}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </>
              ) : (
                <>
                  <span
                    className="inline-flex items-center gap-2 text-sm px-2.5 py-0.5 rounded-full"
                    style={{ backgroundColor: `${c.color}22`, color: c.color }}
                  >
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: c.color }} />
                    {c.name}
                  </span>
                  <span className="flex-1" />
                  <button
                    type="button"
                    onClick={() => startEdit(c)}
                    className="text-xs text-muted-foreground hover:text-foreground rounded-full p-1.5 hover:bg-muted"
                    aria-label="Edit"
                    data-testid={`category-edit-${c.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(c)}
                    className="text-xs text-destructive hover:text-destructive rounded-full p-1.5 hover:bg-muted"
                    aria-label="Delete"
                    data-testid={`category-delete-${c.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const ColorRow = ({ value, onChange, testIdPrefix }) => (
  <div className="flex flex-wrap items-center gap-1">
    {CATEGORY_COLORS.map((c) => (
      <button
        key={c}
        type="button"
        onClick={() => onChange(c)}
        className={`h-6 w-6 rounded-full border-2 ${value === c ? 'border-foreground' : 'border-transparent'}`}
        style={{ backgroundColor: c }}
        aria-label={`Color ${c}`}
        data-testid={`${testIdPrefix}-${c.slice(1)}`}
      />
    ))}
  </div>
);
